import { languageDutch } from "../i18n";
import type { DepartmentStory } from "../types/DepartmentTypes";

/**
 * Normalizes Storyblok paths so comparisons and generated URLs do not depend on extra slashes.
 *
 * @param path - The Storyblok path to normalize.
 * @returns The path without leading, trailing, or duplicate slashes.
 */
export const normalizeStoryblokPath = (path?: string | null) =>
  path?.split("/").filter(Boolean).join("/") ?? "";

/**
 * Removes the final story slug to obtain the Storyblok folder containing the story.
 *
 * @param path - The full Storyblok story path.
 * @returns The normalized parent folder path, or an empty string when no parent exists.
 */
export const getParentStoryblokPath = (path?: string | null) => {
  const segments = normalizeStoryblokPath(path).split("/").filter(Boolean);
  segments.pop();
  return segments.join("/");
};

/**
 * Removes the app language prefix before comparing a localized route with a Storyblok path.
 *
 * @param path - The route or Storyblok path to inspect.
 * @param language - The active application language.
 * @returns The normalized path without its non-Dutch language prefix.
 */
export const stripRouteLanguage = (path: string, language: string) => {
  const segments = normalizeStoryblokPath(path).split("/").filter(Boolean);
  if (language !== languageDutch && segments[0] === language) {
    segments.shift();
  }
  return segments.join("/");
};

/**
 * Gets the folder used by starts_with when fetching sibling Departments.
 *
 * @param story - The Department story whose parent folder is requested.
 * @returns The story's normalized parent folder path.
 */
export const getDefaultDepartmentFolder = (story: DepartmentStory) =>
  getParentStoryblokPath(story.default_full_slug ?? story.full_slug);

/**
 * Gets the current-language folder used for headings and direct-child comparisons.
 *
 * @param story - The Department story whose localized parent folder is required.
 * @param language - The active application language.
 * @returns The localized parent folder path without the app language prefix.
 */
export const getLocalizedDepartmentFolder = (
  story: DepartmentStory,
  language: string
) => stripRouteLanguage(getParentStoryblokPath(story.full_slug), language);

/**
 * Checks whether a Department is directly inside the requested folder, excluding nested descendants.
 *
 * @param story - The Department story to evaluate.
 * @param folderPath - The folder expected to contain the story directly.
 * @param language - The active application language.
 * @returns Whether the story is a direct child of the requested folder.
 */
export const isDirectDepartmentChild = (
  story: DepartmentStory,
  folderPath: string,
  language: string
) =>
  stripRouteLanguage(getParentStoryblokPath(story.full_slug), language) ===
  stripRouteLanguage(folderPath, language);

/**
 * Selects the best available Storyblok path for the requested language.
 *
 * @param story - The Department story to resolve.
 * @param language - The language for which a path is required.
 * @returns The translated path when available, otherwise the best full-slug fallback.
 */
const getLocalizedStoryPath = (story: DepartmentStory, language: string) => {
  if (language === languageDutch) {
    return story.default_full_slug ?? story.full_slug;
  }

  return (
    story.translated_slugs?.find((slug) => slug.lang === language)?.path ??
    story.full_slug ??
    story.default_full_slug
  );
};

/**
 * Builds an application href while adding the language prefix only when the route requires it.
 *
 * @param story - The Department story to link to.
 * @param language - The target application language.
 * @returns An absolute application path for the localized Department story.
 */
export const buildDepartmentHref = (
  story: DepartmentStory,
  language: string
) => {
  const path = normalizeStoryblokPath(getLocalizedStoryPath(story, language));
  if (language === languageDutch || path.startsWith(`${language}/`)) {
    return `/${path}`;
  }
  return `/${language}/${path}`;
};

/**
 * Converts the final folder slug into the visible services-page heading.
 *
 * @param folderPath - The Storyblok folder path used by the services-page instance.
 * @returns A capitalized, space-separated heading derived from the final folder slug.
 */
export const humanizeDepartmentFolder = (folderPath: string) => {
  const folderName = normalizeStoryblokPath(folderPath).split("/").at(-1);
  if (!folderName) return "";

  const label = folderName.replaceAll("-", " ");
  return `${label.charAt(0).toUpperCase()}${label.slice(1)}`;
};
